import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getInactiveStoreSummary } from "../../services";
import { InactiveStoreSummaryResponse } from "../../types/inactive-stores.type";
import { formatThaiShortDate } from "../../utils/date";
import VisitReportTabs from "../VisitFrequency/ReportTabs";
import { INACTIVE_STORES_PATH } from "../VisitFrequency/reportPaths";
import PivotTable from "./PivotTable";
import StoreFilters from "./StoreFilters";
import StoreListDialog from "./StoreListDialog";
import StoreTable from "./StoreTable";
import ThresholdSelector from "./ThresholdSelector";
import {
  DEFAULT_FILTERS,
  LOAD_ERROR_TEXT,
  PivotCellSelection,
  StoreFilterValues,
  reconcileFilters,
  toggleBranchFilter,
} from "./inactiveStores";

interface ThresholdBounds {
  days: number;
  maxDays: number;
}

/**
 * ร้านค้าไม่ได้เข้านาน — which shops the vans have not billed for at least N days, replacing
 * finance's FIN Excel file. A pivot per branch and GROUP on top, the shops themselves below.
 *
 * The pivot always counts every branch in scope; the filters narrow only the detail table and
 * its export. Branch scope is the server's: a branch reader only ever receives their own.
 */
export default function InactiveStores() {
  // null until a threshold is picked: the first request lets the server apply its default
  // (`report.inactive_store.default_days`), and every figure below uses the `days` it answers with.
  const [days, setDays] = useState<number | null>(null);
  // The threshold and lookback of the last answer, kept through a failed reload so the
  // selector stays usable.
  const [threshold, setThreshold] = useState<ThresholdBounds | null>(null);
  const [summary, setSummary] = useState<InactiveStoreSummaryResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [filters, setFilters] = useState<StoreFilterValues>(DEFAULT_FILTERS);
  const [dialogCell, setDialogCell] = useState<PivotCellSelection | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // One request per threshold. The previous summary stays on screen until the next answers, so
  // the detail table keeps its filter options and reloads alongside the pivot, not after it.
  useEffect(() => {
    let isLatest = true;
    setLoading(true);
    setHasError(false);
    getInactiveStoreSummary(days === null ? {} : { days })
      .then((response) => {
        if (!isLatest) return;
        const nextSummary = response.data.data;
        setSummary(nextSummary);
        if (nextSummary) {
          setThreshold({ days: nextSummary.days, maxDays: nextSummary.lookback_days });
          setFilters((current) => reconcileFilters(current, nextSummary));
        }
      })
      .catch(() => {
        // The axios interceptor has already shown the server's message; the page only drops
        // figures that no longer answer the chosen threshold.
        if (!isLatest) return;
        setHasError(true);
        setSummary(null);
      })
      .finally(() => {
        if (isLatest) setLoading(false);
      });
    return () => {
      isLatest = false;
    };
  }, [days]);

  const handleToggleBranch = (branchCode: string) =>
    setFilters((current) => ({
      ...current,
      branchCode: toggleBranchFilter(current.branchCode, branchCode),
    }));

  const handleOpenCell = (selection: PivotCellSelection) => {
    setDialogCell(selection);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  return (
    <Box sx={{ padding: "8px 0" }}>
      <VisitReportTabs current={INACTIVE_STORES_PATH} />

      <Stack spacing={2}>
        <Stack spacing={1}>
          {threshold && (
            <ThresholdSelector
              days={threshold.days}
              maxDays={threshold.maxDays}
              onChange={setDays}
            />
          )}
          {summary && (
            <Typography variant="caption" color="text.secondary">
              ณ วันที่{" "}
              {formatThaiShortDate(summary.ref_date, { weekday: false })} ·
              นับจากบิลย้อนหลัง {summary.lookback_days} วัน (ร้านที่ไม่มีบิลเกิน{" "}
              {summary.lookback_days} วันไม่ถูกนับ)
            </Typography>
          )}
        </Stack>

        {hasError && <Alert severity="error">{LOAD_ERROR_TEXT}</Alert>}

        {loading && (
          <Stack alignItems="center" spacing={1} py={6}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              กำลังโหลด...
            </Typography>
          </Stack>
        )}

        {!loading && summary && (
          <PivotTable
            summary={summary}
            selectedBranchCode={filters.branchCode}
            onToggleBranch={handleToggleBranch}
            onOpenCell={handleOpenCell}
          />
        )}

        {summary && (
          <Paper sx={{ p: 2 }}>
            <Stack mb={2}>
              <Typography variant="subtitle2" fontWeight={700}>
                รายละเอียดร้าน
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ตัวกรองมีผลไฟล์ Export
              </Typography>
            </Stack>
            <StoreFilters
              branches={summary.branches}
              vanOptions={summary.van_options}
              groupOptions={summary.group_options}
              filters={filters}
              onChange={setFilters}
            />
            <StoreTable
              days={summary.days}
              filters={filters}
              refDate={summary.ref_date}
              lookbackDays={summary.lookback_days}
            />
          </Paper>
        )}
      </Stack>

      {summary && (
        <StoreListDialog
          open={isDialogOpen}
          cell={dialogCell}
          days={summary.days}
          lookbackDays={summary.lookback_days}
          onClose={handleCloseDialog}
        />
      )}
    </Box>
  );
}
